(function (root, factory) {
    if (typeof module === 'object' && typeof module.exports === 'object') {
        module.exports = factory(root);
    } else {
        root.ThemeRandomFadeText = factory(root);
    }
})(
    typeof globalThis !== 'undefined'
        ? globalThis
        : typeof window !== 'undefined'
            ? window
            : typeof self !== 'undefined'
                ? self
                : this,
    function (root) {
        const DEFAULT_OPTIONS = {
            selector: '.rfx-random-fade',
            threshold: 0.2,
            rootMargin: '0px 0px -8% 0px',
            once: true,
            autoInit: true,
            charClass: 'rfx-random-fade__char',
            wordClass: 'rfx-random-fade__word',
            spaceClass: 'rfx-random-fade__space',
            doneClass: 'rfx-random-fade--done',
            splitAttr: 'data-rfx-split',
            playedAttr: 'data-rfx-played',
            skipTags: ['SCRIPT', 'STYLE']
        };

        const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

        const shuffle = (array) => {
            const arr = [...array];
            for (let i = arr.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [arr[i], arr[j]] = [arr[j], arr[i]];
            }
            return arr;
        };

        const normalizeText = (text) => text.replace(/\s+/g, ' ').trim();

        function createInstance(userOptions = {}) {
            const options = { ...DEFAULT_OPTIONS, ...userOptions };
            let observer = null;

            const createCharFragment = (text) => {
                const normalized = normalizeText(text);
                const frag = document.createDocumentFragment();
                const chars = [];

                if (!normalized) return { frag, chars };

                let word = document.createElement('span');
                word.className = options.wordClass;

                [...normalized].forEach((char) => {
                    if (char === ' ') {
                        if (word.childNodes.length) {
                            frag.appendChild(word);
                            word = document.createElement('span');
                            word.className = options.wordClass;
                        }

                        const space = document.createElement('span');
                        space.className = options.spaceClass;
                        space.innerHTML = '&nbsp;';
                        frag.appendChild(space);
                        return;
                    }

                    const span = document.createElement('span');
                    span.className = options.charClass;
                    span.textContent = char;
                    word.appendChild(span);
                    chars.push(span);
                });

                if (word.childNodes.length) frag.appendChild(word);

                return { frag, chars };
            };

            const splitNode = (node, chars = []) => {
                if (!node) return chars;

                if (node.nodeType === Node.TEXT_NODE) {
                    const text = node.nodeValue || '';
                    if (!text.trim()) return chars;

                    const parent = node.parentNode;
                    if (!parent) return chars;

                    const { frag, chars: newChars } = createCharFragment(text);
                    parent.replaceChild(frag, node);
                    chars.push(...newChars);
                    return chars;
                }

                if (node.nodeType !== Node.ELEMENT_NODE) return chars;
                if (options.skipTags.includes(node.tagName)) return chars;

                if (
                    node.classList.contains(options.charClass) ||
                    node.classList.contains(options.spaceClass) ||
                    node.classList.contains(options.wordClass)
                ) {
                    return chars;
                }

                const childNodes = [...node.childNodes];
                childNodes.forEach((child) => splitNode(child, chars));

                return chars;
            };

            const splitText = (el) => {
                if (!el) return [];

                if (el.getAttribute(options.splitAttr) === 'true') {
                    return [...el.querySelectorAll(`.${options.charClass}`)];
                }

                el.setAttribute(options.splitAttr, 'true');
                return splitNode(el, []);
            };

            const getTiming = (el, chars) => {
                const rect = el.getBoundingClientRect();
                const vh = window.innerHeight || document.documentElement.clientHeight;
                const vw = window.innerWidth || document.documentElement.clientWidth;

                const centerY = rect.top + rect.height / 2;
                const topWeight = clamp(1 - centerY / vh, 0, 1);
                const areaWeight = clamp((rect.width * rect.height) / (vw * vh * 0.22), 0, 1);
                const textWeight = clamp(1 - chars.length / 90, 0.2, 1);
                const level = clamp(topWeight * 0.45 + areaWeight * 0.35 + textWeight * 0.2, 0, 1);

                return {
                    startDelay: Math.round((1 - topWeight) * 80),
                    spread: Math.round(100 + level * 420),
                    duration: Math.round(160 + level * 160)
                };
            };

            const play = (el) => {
                if (!el || el.getAttribute(options.playedAttr) === 'true') return;

                el.setAttribute(options.playedAttr, 'true');

                const chars = splitText(el);
                if (!chars.length) {
                    el.classList.add(options.doneClass);
                    return;
                }

                const timing = getTiming(el, chars);
                const order = shuffle(chars.map((_, i) => i));
                const step = chars.length > 1 ? timing.spread / (chars.length - 1) : 0;

                order.forEach((charIndex, revealIndex) => {
                    const char = chars[charIndex];
                    char.style.transitionDuration = `${timing.duration}ms`;

                    setTimeout(() => {
                        char.style.opacity = '1';
                    }, timing.startDelay + Math.round(revealIndex * step));
                });

                setTimeout(() => {
                    el.classList.add(options.doneClass);
                }, timing.startDelay + timing.spread + timing.duration + 60);
            };

            const observe = (elements) => {
                if (!elements.length) return;

                elements.forEach((el) => splitText(el));

                if (!('IntersectionObserver' in window)) {
                    elements.forEach(play);
                    return;
                }

                observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        play(entry.target);
                        if (options.once && observer) observer.unobserve(entry.target);
                    });
                }, {
                    threshold: options.threshold,
                    rootMargin: options.rootMargin
                });

                elements.forEach((el) => observer.observe(el));
            };

            const init = (target) => {
                const elements = typeof target === 'string'
                    ? [...document.querySelectorAll(target)]
                    : target instanceof Element
                        ? [target]
                        : target instanceof NodeList || Array.isArray(target)
                            ? [...target]
                            : [...document.querySelectorAll(options.selector)];

                if (!elements.length) return;
                observe(elements);
            };

            const reset = (el) => {
                if (!el) return;

                el.setAttribute(options.playedAttr, 'false');
                el.classList.remove(options.doneClass);

                el.querySelectorAll(`.${options.charClass}`).forEach((char) => {
                    char.style.opacity = '0';
                });
            };

            const destroy = () => {
                if (observer) {
                    observer.disconnect();
                    observer = null;
                }
            };

            return {
                init,
                play,
                reset,
                splitText,
                destroy
            };
        }

        const api = {
            create: createInstance
        };

        if (typeof document !== 'undefined' && DEFAULT_OPTIONS.autoInit) {
            const boot = () => {
                const instance = createInstance();
                instance.init();
                root.__ThemeRandomFadeTextInstance__ = instance;
            };

            if (document.readyState === 'loading') {
                document.addEventListener('DOMContentLoaded', boot);
            } else {
                boot();
            }

            document.addEventListener('DOMContentLoaded', () => {
                document.body.classList.add('opacity-is-ready');
            });
        }

        return api;
    }
);

const ScrollRevealManager = (() => {
    const config = {
        classNames: ['reveal-on-scroll', 'reveal-fade-in','reveal-on-shift','reveal-backdrop-filter'],
        activeClass: 'is-visible',
        threshold: 0.1,
        baseDelay: 0.1,
    };

    let observer = null;

    const getSelector = () => {
        return config.classNames.map(className => `.${className}`).join(', ');
    };

    const getObserver = () => {
        if (!observer) {
            observer = new IntersectionObserver((entries) => {
                let delayCount = 0;
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        const el = entry.target;
                        if (!el.classList.contains(config.activeClass)) {
                            el.style.transitionDelay = `${delayCount * config.baseDelay}s`;
                            el.classList.add(config.activeClass);
                            observer.unobserve(el);
                            delayCount++;
                        }
                    }
                });
            }, { threshold: config.threshold });
        }
        return observer;
    };

    const core = {
        scan(container = document) {
            const elements = container.querySelectorAll(getSelector());
            const obs = getObserver();

            elements.forEach(el => {
                if (!el.classList.contains(config.activeClass)) {
                    obs.observe(el);
                }
            });
        },

        reveal(container = document) {
            const elements = container.querySelectorAll(getSelector());
            elements.forEach(el => {
                el.style.transitionDelay = '0s';
                el.classList.add(config.activeClass);
                if (observer) observer.unobserve(el);
            });
        }
    };

    const bindEvents = () => {
        document.addEventListener('DOMContentLoaded', () => core.scan());

        if (window.Shopify && Shopify.designMode) {
            document.addEventListener('shopify:section:load', (e) => core.scan(e.target));
            document.addEventListener('shopify:section:select', (e) => core.reveal(e.target));
            document.addEventListener('shopify:block:select', (e) => core.reveal(e.target));
        }
    };

    bindEvents();

    return {
        update: core.scan,
        showAll: core.reveal
    };
})();